import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
} from '@tanstack/react-table';

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';

import { DataTableToolbar } from './data-table-toolbar';
import { DataTablePagination } from './data-table-pagination';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { Heading } from '@/components/ui/shared/heading';
import { Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocation } from 'react-router-dom';

export function DataTable({
  title,
  columns,
  data,
  filterableColumns = [],
  facetedFilters = [],
  onStatusFilterChange,
  loading = false,
  preselectedFutureVisits = false,
  collapsible = false,
  isStartOpen = false, // (kept for compatibility)
  onRowClick,
  isOpen,
  defaultOpen = false,
  onOpenChange,
  onPageChange,
  persistId,
  initialColumnFilters = [],
  subTitle,
}) {
  const location = useLocation();

  const scopeRoot = React.useMemo(() => {
    if (persistId) return persistId;

    const p = location.pathname.replace(/\/+$/, '');
    const parts = p.split('/').filter(Boolean);

    if (parts[0] === 'userAccount' && parts[1]) {
      return `/${parts[0]}/${parts[1]}`;
    }

    return p; // fallback
  }, [location.pathname, persistId]);

  const pageKey = `datatable:${scopeRoot}:pageIndex`;
  const filtersKey = `datatable:${scopeRoot}:columnFilters`;
  const globalKey = `datatable:${scopeRoot}:globalFilter`;
  const sortingKey = `datatable:${scopeRoot}:sorting`;
  const visibilityKey = `datatable:${scopeRoot}:columnVisibility`;

  const [rowSelection, setRowSelection] = React.useState({});

  const [columnVisibility, setColumnVisibility] = React.useState(() => {
    try {
      const raw = sessionStorage.getItem(visibilityKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const [columnFilters, setColumnFilters] = React.useState(() => {
    try {
      const raw = sessionStorage.getItem(filtersKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [sorting, setSorting] = React.useState(() => {
    return [];
    // try {
    //   const raw = sessionStorage.getItem(sortingKey);
    //   return raw ? JSON.parse(raw) : [];
    // } catch {
    //   return [];
    // }

    // Uncomment if sorting needs to be enabled.
  });

  const [globalFilter, setGlobalFilter] = React.useState(() => {
    return sessionStorage.getItem(globalKey) ?? '';
  });

  const savedPageIndex = React.useMemo(() => {
    const raw = sessionStorage.getItem(pageKey);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [pageKey]);

  React.useEffect(() => {
    if (!initialColumnFilters?.length) return;

    const hasSavedFilters = !!sessionStorage.getItem(filtersKey);
    if (hasSavedFilters) return;

    if (columnFilters.length > 0) return;
    setColumnFilters(initialColumnFilters);
  }, [initialColumnFilters, columnFilters.length, filtersKey]);

  React.useEffect(() => {
    const statusFilter = columnFilters.find((f) => f.id === 'status');
    onStatusFilterChange?.(statusFilter?.value || 'history');
  }, [columnFilters, onStatusFilterChange]);

  React.useEffect(() => {
    if (!preselectedFutureVisits) return;

    setColumnFilters((prev) => {
      const alreadyFiltered = prev.some((f) => f.id === 'status');
      if (alreadyFiltered) return prev;
      return [...prev, { id: 'status', value: 'registrations' }];
    });
  }, [preselectedFutureVisits]);

  const globalFilterFn = (row, _columnId, filterValue) => {
    const search = filterValue?.toLowerCase();
    return filterableColumns.some((key) => {
      const rawValue = row.getValue(key);
      if (!rawValue) return false;

      let formattedValue = '';
      const date = new Date(rawValue);
      const isValidDate = !isNaN(date.getTime());

      if (isValidDate && typeof rawValue !== 'string') {
        formattedValue = date.toISOString().slice(0, 10);
      } else {
        formattedValue = rawValue.toString().toLowerCase();
      }

      return formattedValue.includes(search);
    });
  };

  const [internalCollapsed, setInternalCollapsed] = React.useState(
    collapsible ? !defaultOpen : false,
  );

  const collapsed = collapsible
    ? isOpen !== undefined
      ? !isOpen
      : internalCollapsed
    : false;

  const setCollapsed = (nextCollapsedOrFn) => {
    const nextCollapsed =
      typeof nextCollapsedOrFn === 'function'
        ? nextCollapsedOrFn(collapsed)
        : nextCollapsedOrFn;

    const nextOpen = !nextCollapsed;
    onOpenChange?.(nextOpen);

    if (isOpen === undefined) {
      setInternalCollapsed(nextCollapsed);
    }
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 25,
        pageIndex: savedPageIndex,
      },
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn,
  });

  React.useEffect(() => {
    sessionStorage.setItem(filtersKey, JSON.stringify(columnFilters ?? []));
  }, [columnFilters, filtersKey]);

  React.useEffect(() => {
    sessionStorage.setItem(globalKey, globalFilter ?? '');
  }, [globalFilter, globalKey]);

  React.useEffect(() => {
    sessionStorage.setItem(sortingKey, JSON.stringify(sorting ?? []));
  }, [sorting, sortingKey]);

  React.useEffect(() => {
    sessionStorage.setItem(
      visibilityKey,
      JSON.stringify(columnVisibility ?? {}),
    );
  }, [columnVisibility, visibilityKey]);

  React.useEffect(() => {
    const current = table.getState().pagination.pageIndex;
    sessionStorage.setItem(pageKey, String(current));
    onPageChange?.(current);
  }, [table.getState().pagination.pageIndex, pageKey, onPageChange]);

  const lastFiltersJsonRef = React.useRef(null);
  React.useEffect(() => {
    const json = JSON.stringify(columnFilters ?? []);

    if (lastFiltersJsonRef.current === null) {
      lastFiltersJsonRef.current = json;
      return;
    }

    if (lastFiltersJsonRef.current !== json) {
      lastFiltersJsonRef.current = json;
      table.setPageIndex(0);
      sessionStorage.setItem(pageKey, '0');
    }
  }, [columnFilters, table, pageKey]);

  React.useEffect(() => {
    const pageCount = table.getPageCount();
    const current = table.getState().pagination.pageIndex;
    if (pageCount > 0 && current > pageCount - 1) {
      table.setPageIndex(pageCount - 1);
    }
  }, [data, table]);

  React.useEffect(() => {
    const p = location.pathname.replace(/\/+$/, '');
    const stillInSameSection = p.startsWith(scopeRoot);
    if (stillInSameSection) return;

    sessionStorage.removeItem(pageKey);
    sessionStorage.removeItem(filtersKey);
    sessionStorage.removeItem(globalKey);
    sessionStorage.removeItem(sortingKey);
    sessionStorage.removeItem(visibilityKey);
  }, [
    location.pathname,
    scopeRoot,
    pageKey,
    filtersKey,
    globalKey,
    sortingKey,
    visibilityKey,
  ]);

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        {/* LEFT: title + subtitle stacked */}
        <div className="flex flex-col">
          <Heading variant="userAccountTitle">{title}</Heading>
          {subTitle && (
            <p className="text-sm text-muted-foreground mt-1 italic">
              {subTitle}
            </p>
          )}
        </div>

        {/* RIGHT: collapse button */}
        {collapsible && (
          <Button variant="light" onClick={() => setCollapsed((prev) => !prev)}>
            {collapsed ? 'Išskleisti' : 'Suskleisti'}
            <ChevronDown
              className={`ml-1 h-4 w-4 transition-transform ${
                collapsed ? 'rotate-[-90deg]' : ''
              }`}
            />
          </Button>
        )}
      </CardHeader>

      {!collapsed && (
        <>
          <CardContent>
            <div className="flex flex-col gap-4">
              <DataTableToolbar
                table={table}
                facetedFilters={facetedFilters}
                showSearch={true}
                searchValue={globalFilter}
                onSearchChange={setGlobalFilter}
              />

              <div>
                <Table>
                  <TableHeader className="bg-gray-50">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id} colSpan={header.colSpan}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>

                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-40 text-center"
                        >
                          <div className="flex items-center justify-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            <span>Kraunama...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={
                            row.getIsSelected() ? 'selected' : undefined
                          }
                          onClick={() => onRowClick?.(row.original)}
                          className="hover:bg-muted cursor-pointer transition-colors"
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          Nėra rezultatų.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>

          <CardFooter>
            <DataTablePagination table={table} />
          </CardFooter>
        </>
      )}
    </Card>
  );
}
